import { notifier } from "../../core/notifier/notifier";
import { spinner } from "../../util/spinner/spinner";
import { formValidator } from "../../util/form-validator";
import {
    UserEditApi,
    requestUserAddressLookupByZip,
    type UserEditAddressLookupResponse,
    type UserEditUpdateProfileRequest,
    type UserEditUpdateProfileResponse,
} from "./user-edit-api";
import { UserEditListener } from "./user-edit-listener";
import { phpTranslate } from "../../core/php-data-access";
import { ChoicesControl } from "../../components/choices-control/choices-control-setup";
import { OptionSelector } from "../../components/option-selector/option-selector";
import { setValue } from "../../master-data-edit/master-data-edit-form";
import { ModalNestedPosition } from "../../util/modal-nested-position";
import { UserEditRender } from "./user-edit-render";
import { userEditForm } from "./user-edit-form";
import { CommonUtils } from "../../util/common-utils";

interface MasterDataEditPrefectureOption {
    value: string;
    label: string;
    translate: string;
}

interface MasterDataEditControllerLike {
    form: HTMLFormElement | null;
    modal: HTMLElement | null;
    resource: string;
    _userPrefecturePendingValue?: string;
}

export class UserEditHandler {
    public toggleBtn: HTMLButtonElement | null;

    public displayMode: HTMLElement | null;

    public editMode: HTMLElement | null;

    public editForm: HTMLFormElement | null;

    public cancelBtn: HTMLButtonElement | null;

    public saveBtn: HTMLButtonElement | null;

    public accountInfoSection: HTMLElement | null;

    public listener: UserEditListener;

    public constructor() {
        this.toggleBtn = userEditForm.getToggleButton(document);
        this.displayMode = userEditForm.getDisplayMode(document);
        this.editMode = userEditForm.getEditMode(document);
        this.editForm = userEditForm.getEditForm(document);
        this.cancelBtn = userEditForm.getCancelButton(document);
        this.saveBtn = userEditForm.getSaveButton(document);
        this.accountInfoSection = userEditForm.getAccountInfoSection(document);

        this.listener = new UserEditListener({
            onEnterEdit: () => this.enterEditMode(),
            onExitEdit: () => this.exitEditMode(),
            onSubmit: (event) => this.handleSave(event),
            onKeydown: (event) => this.handleKeydown(event),
        });
    }

    public init(): void {
        if (!(this.toggleBtn instanceof HTMLElement)) return;
        if (!(this.displayMode instanceof HTMLElement)) return;
        if (!(this.editMode instanceof HTMLElement)) return;
        if (!(this.editForm instanceof HTMLFormElement)) return;
        if (!(this.accountInfoSection instanceof HTMLElement)) return;

        ChoicesControl.init(this.editForm);
        OptionSelector.init(this.editForm);

        this.listener.bind({
            toggleBtn: this.toggleBtn,
            cancelBtn: this.cancelBtn,
            editForm: this.editForm,
        });
    }

    public isEditModeVisible(): boolean {
        return this.editMode instanceof HTMLElement && this.editMode.style.display !== "none";
    }

    public handleKeydown(event: KeyboardEvent): void {
        if (!this.isEditModeVisible()) return;
        if (event.isComposing) return;

        if (event.key === "Escape") {
            event.preventDefault();
            this.exitEditMode();
            return;
        }

        if (event.key !== "Enter") return;
        if (event.shiftKey || event.ctrlKey || event.metaKey || event.altKey) return;

        const target = event.target instanceof HTMLElement ? event.target : null;
        const tag = (target?.tagName || "").toLowerCase();
        if (tag === "textarea") return;

        event.preventDefault();

        if (this.saveBtn instanceof HTMLButtonElement) {
            this.saveBtn.click();
            return;
        }

        if (this.editForm instanceof HTMLFormElement && this.editForm.requestSubmit) {
            this.editForm.requestSubmit();
        } else {
            this.editForm?.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true }));
        }
    }

    public enterEditMode(): void {
        if (!(this.displayMode instanceof HTMLElement)) return;
        if (!(this.editMode instanceof HTMLElement)) return;
        if (!(this.accountInfoSection instanceof HTMLElement)) return;
        if (!(this.toggleBtn instanceof HTMLElement)) return;

        this.displayMode.style.display = "none";
        this.editMode.style.display = "block";
        this.accountInfoSection.style.display = "none";
        this.toggleBtn.style.display = "none";

        const firstField = this.editForm ? userEditForm.getFirstField(this.editForm) : null;
        if (firstField instanceof HTMLElement) {
            firstField.focus();
        }
    }

    public exitEditMode(): void {
        if (!(this.displayMode instanceof HTMLElement)) return;
        if (!(this.editMode instanceof HTMLElement)) return;
        if (!(this.accountInfoSection instanceof HTMLElement)) return;
        if (!(this.toggleBtn instanceof HTMLElement)) return;

        this.displayMode.style.display = "block";
        this.editMode.style.display = "none";
        this.accountInfoSection.style.display = "block";
        this.toggleBtn.style.display = "block";
    }

    public async handleSave(event: Event): Promise<void> {
        event.preventDefault();
        event.stopImmediatePropagation();

        if (!(this.editForm instanceof HTMLFormElement)) return;

        const validation = formValidator.validate(this.editForm, { showNotification: false });
        if (!validation.isValid) {
            const message = validation.firstError || "required_field";
            notifier.warn(message, { duration: 5000 });
            return;
        }

        const submitBtn = userEditForm.getSubmitButton(this.editForm);
        if (submitBtn instanceof HTMLButtonElement) {
            spinner.showButtonLoader(submitBtn, { translateKey: "spinner_proccesing" });
        }

        const formData = new FormData(this.editForm);
        const userId = Number(window.location.pathname.split("/").pop() || 0);
        const rawRequestData = Object.fromEntries(formData.entries()) as Record<string, unknown>;
        delete rawRequestData._token;
        const requestData = rawRequestData as UserEditUpdateProfileRequest;

        if (!Number.isFinite(userId) || userId <= 0) {
            notifier.error("master_data_user_update_failed");
            return;
        }

        try {
            const response = await UserEditApi.updateProfile(userId, requestData);

            if (response.ok) {
                const data = (await response.json()) as UserEditUpdateProfileResponse;
                if (data.success) {
                    notifier.success("master_data_user_updated");
                    setTimeout(() => {
                        window.location.reload();
                    }, 1000);
                } else {
                    throw new Error(data.message || "Update failed");
                }
                return;
            }

            await notifier.fromResponse(response, "profile_update_error");
            const errorData = (await response.json().catch(() => ({}))) as UserEditUpdateProfileResponse;
            throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
        } catch (error) {
            console.error("Profile update error:", error);
            notifier.error("master_data_user_update_failed");
        } finally {
            if (submitBtn instanceof HTMLButtonElement) {
                spinner.hideButtonLoader(submitBtn);
            }
        }
    }
}

export async function lookupUserAddressByZipCodeForMasterDataEdit(
    controller: MasterDataEditControllerLike,
    rawZipCode: string,
): Promise<void> {
    if (!(controller.form instanceof HTMLFormElement) || controller.resource !== "users") return;

    const zip = String(rawZipCode || "")
        .replace(/\D+/g, "")
        .trim();
    if (zip.length !== 5) return;

    let response: Response;
    try {
        response = await requestUserAddressLookupByZip(zip);
    } catch {
        return;
    }

    if (!response.ok) return;

    const json = (await response.json().catch(() => null)) as UserEditAddressLookupResponse | null;
    const success = json?.success;
    if (success !== 1) return;

    const payload = json?.data || {};
    const inferredPrefecture = resolveUserPrefectureFromLookupPayloadForMasterDataEdit(controller, payload);
    if (inferredPrefecture) {
        setValue(controller.form, "prefecture", inferredPrefecture);
        renderUserPrefectureDisplayForMasterDataEdit(controller, inferredPrefecture);
    }

    const addressEl = controller.form.elements.namedItem("address");
    const currentAddress =
        addressEl instanceof HTMLInputElement || addressEl instanceof HTMLTextAreaElement
            ? String(addressEl.value || "").trim()
            : "";

    if (!currentAddress && payload.address) {
        setValue(controller.form, "address", payload.address);
    }
}

export function getUserPrefectureOptionsForMasterDataEdit(
    controller: MasterDataEditControllerLike,
): MasterDataEditPrefectureOption[] {
    if (!(controller.modal instanceof HTMLElement)) return [];
    const source = userEditForm.getPrefectureOptionsScript(controller.modal);
    if (!(source instanceof HTMLScriptElement)) return [];
    try {
        const parsed = JSON.parse(source.textContent || "[]") as Array<Record<string, UserEditMasterDataValue>>;
        if (!Array.isArray(parsed)) return [];
        return parsed
            .map((entry) => ({
                value: String(entry?.value ?? "").trim(),
                label: String(entry?.label ?? "").trim(),
                translate: String(entry?.translate ?? "").trim(),
            }))
            .filter((entry) => entry.value !== "" && entry.label !== "");
    } catch {
        return [];
    }
}

type UserEditMasterDataValue = unknown;

export function normalizeUserPrefectureMatchingTextForMasterDataEdit(raw: UserEditMasterDataValue): string {
    return String(raw ?? "")
        .trim()
        .toLowerCase()
        .replace(/^(จังหวัด|จ\.|เขต|อำเภอ|อ\.)\s*/u, "")
        .replace(/\s+/g, " ");
}

export function translateUserPrefectureLabelForMasterDataEdit(
    entry: { label?: string; translate?: string } | null | undefined,
): string {
    if (!entry || typeof entry !== "object") return "";
    const fallback = String(entry.label ?? "").trim();
    const key = String(entry.translate ?? "").trim();
    if (!key) return fallback;

    const currentLang = String(document.documentElement.lang || "th").toLowerCase();
    const forcedLang = currentLang === "en" || currentLang === "ja" ? "en" : "th";
    const translated = String(phpTranslate(key, forcedLang) ?? "").trim();
    if (translated && translated !== key) {
        return translated;
    }
    return fallback;
}

export function getUserPrefectureOptionForMasterDataEdit(
    controller: MasterDataEditControllerLike,
    value: UserEditMasterDataValue,
): MasterDataEditPrefectureOption | null {
    const normalized = String(value ?? "").trim();
    if (!normalized) return null;
    const options = getUserPrefectureOptionsForMasterDataEdit(controller);
    return options.find((entry) => entry.value === normalized) || null;
}

export function getUserPrefectureLabelForMasterDataEdit(
    controller: MasterDataEditControllerLike,
    value: UserEditMasterDataValue,
): string {
    const normalized = String(value ?? "").trim();
    if (!normalized) return "-";
    const options = getUserPrefectureOptionsForMasterDataEdit(controller);
    const found = options.find((entry) => entry.value === normalized);
    return found ? translateUserPrefectureLabelForMasterDataEdit(found) : normalized;
}

export function resolveUserPrefectureFromLookupPayloadForMasterDataEdit(
    controller: MasterDataEditControllerLike,
    payload: Record<string, unknown>,
): string {
    const options = getUserPrefectureOptionsForMasterDataEdit(controller);
    if (!Array.isArray(options) || options.length === 0) return "";

    const rawCandidates = [payload?.city, payload?.address, payload?.state, payload?.province]
        .map((value) => String(value ?? "").trim())
        .filter((value) => value !== "");
    if (rawCandidates.length === 0) return "";

    const candidates = rawCandidates.map((value) => normalizeUserPrefectureMatchingTextForMasterDataEdit(value));

    const optionRows = options.map((entry) => {
        const translateKey = String(entry?.translate ?? "").trim();
        const englishLabel = translateKey ? String(phpTranslate(translateKey, "en") ?? "").trim() : "";

        return {
            value: String(entry?.value ?? "").trim(),
            thaiLabel: normalizeUserPrefectureMatchingTextForMasterDataEdit(entry?.label ?? ""),
            thaiValue: normalizeUserPrefectureMatchingTextForMasterDataEdit(entry?.value ?? ""),
            englishLabel: normalizeUserPrefectureMatchingTextForMasterDataEdit(englishLabel),
        };
    });

    const exact = optionRows.find((row) =>
        candidates.some(
            (candidate) =>
                candidate !== "" &&
                (candidate === row.thaiValue ||
                    candidate === row.thaiLabel ||
                    (row.englishLabel !== "" && candidate === row.englishLabel)),
        ),
    );
    if (exact?.value) return exact.value;

    const partial = optionRows.find((row) =>
        candidates.some((candidate) => {
            if (!candidate) return false;
            if (row.thaiValue && (candidate.includes(row.thaiValue) || row.thaiValue.includes(candidate))) return true;
            if (row.thaiLabel && (candidate.includes(row.thaiLabel) || row.thaiLabel.includes(candidate))) return true;
            if (row.englishLabel && (candidate.includes(row.englishLabel) || row.englishLabel.includes(candidate))) {
                return true;
            }
            return false;
        }),
    );

    return partial?.value || "";
}

export function renderUserPrefectureDisplayForMasterDataEdit(
    controller: MasterDataEditControllerLike,
    value: UserEditMasterDataValue,
): void {
    const entry = getUserPrefectureOptionForMasterDataEdit(controller, value);
    const label = getUserPrefectureLabelForMasterDataEdit(controller, value);
    UserEditRender.renderUserPrefectureDisplayForMasterDataEdit(controller, value, entry, label);
}

export function renderUserPrefectureModalListForMasterDataEdit(
    controller: MasterDataEditControllerLike,
    filterText = "",
): void {
    if (!(controller.modal instanceof HTMLElement) || !(controller.form instanceof HTMLFormElement)) return;

    const keyword = String(filterText || "")
        .trim()
        .toLowerCase();
    const options = getUserPrefectureOptionsForMasterDataEdit(controller).filter((entry) => {
        if (!keyword) return true;
        return entry.label.toLowerCase().includes(keyword) || entry.value.toLowerCase().includes(keyword);
    });

    const selected = controller.form.elements.namedItem("prefecture");
    const selectedValue = selected instanceof HTMLInputElement ? String(selected.value || "") : "";
    const activeValue = String(controller._userPrefecturePendingValue || "").trim() || selectedValue;

    UserEditRender.renderUserPrefectureModalListForMasterDataEdit(
        controller,
        options,
        activeValue,
        translateUserPrefectureLabelForMasterDataEdit,
    );
}

export function openUserPrefectureModalForMasterDataEdit(controller: MasterDataEditControllerLike): void {
    if (!(controller.modal instanceof HTMLElement)) return;
    const overlayEl = userEditForm.getPrefectureModalOverlay(controller.modal);
    const modalEl = userEditForm.getPrefectureModal(controller.modal);
    if (!(modalEl instanceof HTMLElement)) return;

    const filterEl = userEditForm.getPrefectureModalFilter(modalEl);
    const selected = controller.form?.elements?.namedItem("prefecture");
    const selectedValue = selected instanceof HTMLInputElement ? String(selected.value || "") : "";
    controller._userPrefecturePendingValue = selectedValue;
    if (overlayEl instanceof HTMLElement) {
        ModalNestedPosition.openOverlayModal({
            overlayEl,
            modalEl,
            overlayDisplayValue: "block",
            modalDisplayValue: "block",
        });
    } else {
        ModalNestedPosition.openModal({
            modalEl,
            displayValue: "block",
        });
    }

    renderUserPrefectureModalListForMasterDataEdit(controller, "");
    if (filterEl instanceof HTMLInputElement) {
        filterEl.value = "";
        filterEl.focus();
    }
}

export function closeUserPrefectureModalForMasterDataEdit(controller: MasterDataEditControllerLike): void {
    if (!(controller.modal instanceof HTMLElement)) return;
    const overlayEl = userEditForm.getPrefectureModalOverlay(controller.modal);
    const modalEl = userEditForm.getPrefectureModal(controller.modal);
    controller._userPrefecturePendingValue = "";
    if (!(modalEl instanceof HTMLElement)) return;
    if (overlayEl instanceof HTMLElement) {
        ModalNestedPosition.closeOverlayModal({
            overlayEl,
            modalEl,
        });
        return;
    }
    ModalNestedPosition.closeModal({ modalEl });
}

export function clearUserPrefecturePendingValueForMasterDataEdit(controller: MasterDataEditControllerLike): void {
    controller._userPrefecturePendingValue = "";
    renderUserPrefectureModalListForMasterDataEdit(controller, "");
}

export function applyUserPrefecturePendingValueForMasterDataEdit(controller: MasterDataEditControllerLike): void {
    if (!(controller.form instanceof HTMLFormElement)) return;
    setValue(controller.form, "prefecture", controller._userPrefecturePendingValue || "");
    renderUserPrefectureDisplayForMasterDataEdit(controller, controller._userPrefecturePendingValue || "");
    closeUserPrefectureModalForMasterDataEdit(controller);
}
